/**
 * Voice-Activated Medication Command Interface
 * 
 * Enables physicians to approve/reject medications using voice commands:
 * - "Approve all medications for patient [name]"
 * - "Approve [medication name]"
 * - "Reject [medication name]"
 * - "Show pending orders"
 * - "Send to pharmacy"
 * 
 * Integrates with MedicationWorkflowDashboard for one-click → voice approval
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import {
  Mic, MicOff, Volume2, VolumeX, CheckCircle, XCircle,
  Loader2, AlertTriangle, Pill, Brain, Sparkles,
  Command, HelpCircle
} from "lucide-react";
import { useSpeechToText } from "@/components/SpeechToText";

const API = process.env.REACT_APP_BACKEND_URL;

// Voice command patterns
const COMMAND_PATTERNS = {
  approve_all: /^(approve|accept|confirm|sign)\s+(all|everything|bundle)/i,
  approve_med: /^(approve|accept|confirm|sign)\s+(.+)/i,
  reject_med: /^(reject|deny|decline|cancel)\s+(.+)/i,
  show_queue: /^(show|list|get|what are|display)\s+(pending|queue|orders)/i,
  send_pharmacy: /^(send|push|submit)\s+(to\s+)?pharmacy/i,
  help: /^(help|commands|what can you do)/i,
  stop: /^(stop|cancel|nevermind|quit)/i,
};

// Sample voice prompts for users
const VOICE_PROMPTS = [
  "Approve all medications",
  "Approve morphine for Smith",
  "Reject hydrocodone",
  "Show pending orders",
  "Send to pharmacy",
  "Help"
];

export default function VoiceMedCommand({ 
  physicianQueue = [], 
  selectedBundle = null,
  onApproveAll,
  onApproveMed,
  onRejectMed,
  onRefresh,
  onSendToPharmacy,
  onSelectBundle
}) {
  const [isActive, setIsActive] = useState(false);
  const [lastCommand, setLastCommand] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [commandHistory, setCommandHistory] = useState([]);
  const [showHelp, setShowHelp] = useState(false);
  
  // Speech recognition
  const {
    isListening,
    transcript,
    interimTranscript,
    isSupported,
    error,
    startListening,
    stopListening,
    clearTranscript
  } = useSpeechToText({
    continuous: true,
    interimResults: true,
    neverAbort: true,
    silentErrors: true,
    onTranscript: handleVoiceTranscript,
    maxSilenceMs: 30000
  });

  // Process voice transcript
  function handleVoiceTranscript(text) {
    if (!text || text.trim().length < 2) return;
    
    const command = text.trim().toLowerCase();
    setLastCommand(command);
    
    // Add to history
    setCommandHistory(prev => [...prev.slice(-9), { text: command, time: new Date() }]);
    
    // Process command
    processCommand(command);
  }

  // Process voice command
  const processCommand = useCallback(async (command) => {
    setProcessing(true);
    
    try {
      // Check for stop command
      if (COMMAND_PATTERNS.stop.test(command)) {
        toast.info("Voice command cancelled");
        stopListening();
        setIsActive(false);
        return;
      }

      // Check for help
      if (COMMAND_PATTERNS.help.test(command)) {
        setShowHelp(true);
        speakResponse("Available commands: Approve all, Approve medication name, Reject medication name, Show pending orders, Send to pharmacy");
        return;
      }

      // Check for show queue
      if (COMMAND_PATTERNS.show_queue.test(command)) {
        if (onRefresh) await onRefresh();
        const count = physicianQueue.length;
        speakResponse(`You have ${count} pending medication ${count === 1 ? 'bundle' : 'bundles'} to review`);
        return;
      }

      // Check for send to pharmacy
      if (COMMAND_PATTERNS.send_pharmacy.test(command)) {
        if (onSendToPharmacy) {
          await onSendToPharmacy();
          speakResponse("Orders sent to pharmacy");
        } else {
          speakResponse("No orders ready to send");
        }
        return;
      }

      // Check for approve all
      if (COMMAND_PATTERNS.approve_all.test(command)) {
        if (selectedBundle) {
          if (onApproveAll) {
            await onApproveAll(selectedBundle.id);
            speakResponse(`Approved all ${selectedBundle.recommendations?.length || 0} medications`);
          }
        } else if (physicianQueue.length > 0) {
          // Select first bundle and approve
          const bundle = physicianQueue[0];
          if (onSelectBundle) onSelectBundle(bundle);
          if (onApproveAll) {
            await onApproveAll(bundle.id);
            speakResponse(`Approved all medications for ${bundle.patient_name || 'patient'}`);
          }
        } else {
          speakResponse("No pending orders to approve");
        }
        return;
      }

      // Check for approve specific medication
      const approveMatch = command.match(COMMAND_PATTERNS.approve_med);
      if (approveMatch) {
        const medName = approveMatch[2].trim();
        
        // Find matching medication in current bundle or queue
        let found = false;
        const searchBundles = selectedBundle ? [selectedBundle] : physicianQueue;
        
        for (const bundle of searchBundles) {
          for (const rec of (bundle.recommendations || [])) {
            if (rec.medication_name.toLowerCase().includes(medName) ||
                rec.generic_name?.toLowerCase().includes(medName)) {
              if (onApproveMed) {
                await onApproveMed(rec.id, bundle.id);
                speakResponse(`Approved ${rec.medication_name}`);
                found = true;
                break;
              }
            }
          }
          if (found) break;
        }
        
        if (!found) {
          speakResponse(`Could not find medication matching ${medName}`);
        }
        return;
      }

      // Check for reject specific medication
      const rejectMatch = command.match(COMMAND_PATTERNS.reject_med);
      if (rejectMatch) {
        const medName = rejectMatch[2].trim();
        
        let found = false;
        const searchBundles = selectedBundle ? [selectedBundle] : physicianQueue;
        
        for (const bundle of searchBundles) {
          for (const rec of (bundle.recommendations || [])) {
            if (rec.medication_name.toLowerCase().includes(medName) ||
                rec.generic_name?.toLowerCase().includes(medName)) {
              if (onRejectMed) {
                await onRejectMed(rec.id, bundle.id);
                speakResponse(`Rejected ${rec.medication_name}`);
                found = true;
                break;
              }
            }
          }
          if (found) break;
        }
        
        if (!found) {
          speakResponse(`Could not find medication matching ${medName}`);
        }
        return;
      }

      // Unrecognized command
      speakResponse("I didn't understand that command. Say help for available options.");
      
    } catch (err) {
      console.error("Command processing error:", err);
      speakResponse("Error processing command. Please try again.");
    } finally {
      setProcessing(false);
    }
  }, [physicianQueue, selectedBundle, onApproveAll, onApproveMed, onRejectMed, onRefresh, onSendToPharmacy, onSelectBundle, stopListening]);

  // Text-to-speech response
  const speakResponse = (text) => {
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 1.1;
      utterance.pitch = 1;
      window.speechSynthesis.speak(utterance);
    }
    toast.info(text);
  };

  // Toggle voice activation
  const toggleVoice = () => {
    if (isActive) {
      stopListening();
      setIsActive(false);
    } else {
      clearTranscript();
      startListening();
      setIsActive(true);
      speakResponse("Voice commands activated. Say a command or help for options.");
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (isListening) stopListening();
      window.speechSynthesis?.cancel();
    };
  }, [isListening, stopListening]);

  if (!isSupported) {
    return (
      <Card className="border-amber-200 bg-amber-50">
        <CardContent className="pt-4">
          <div className="flex items-center gap-2 text-amber-700">
            <AlertTriangle className="h-5 w-5" />
            <p className="text-sm">Voice commands not supported in this browser. Use Chrome or Edge.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={`transition-all ${isActive ? 'ring-2 ring-purple-500 bg-purple-50/50' : ''}`}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Brain className="h-5 w-5 text-purple-600" />
            Voice Commands
          </div>
          <Button
            variant={isActive ? "destructive" : "default"}
            size="sm"
            onClick={toggleVoice}
            className={isActive ? "animate-pulse" : ""}
            data-testid="voice-command-toggle"
          >
            {isActive ? (
              <>
                <MicOff className="h-4 w-4 mr-2" />
                Stop Listening
              </>
            ) : (
              <>
                <Mic className="h-4 w-4 mr-2" />
                Start Voice
              </>
            )}
          </Button>
        </CardTitle>
        <CardDescription>
          Approve medications hands-free • Target: ≤30s per bundle
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Active listening indicator */}
        {isActive && (
          <div className="p-3 bg-purple-100 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
              <span className="text-sm font-medium text-purple-800">Listening...</span>
              {processing && <Loader2 className="h-4 w-4 animate-spin text-purple-600" />}
            </div>
            
            {/* Current transcript */}
            <div className="min-h-[40px] p-2 bg-white rounded border">
              <p className="text-sm">
                <span className="text-gray-800">{transcript}</span>
                <span className="text-gray-400 italic">{interimTranscript}</span>
              </p>
            </div>
          </div>
        )}

        {/* Last command result */}
        {lastCommand && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Command className="h-4 w-4" />
            <span>Last: "{lastCommand}"</span>
          </div>
        )}

        {/* Quick prompts */}
        <div>
          <p className="text-xs text-gray-500 mb-2">Try saying:</p>
          <div className="flex flex-wrap gap-2">
            {VOICE_PROMPTS.map((prompt, idx) => (
              <Badge 
                key={idx} 
                variant="outline" 
                className="text-xs cursor-pointer hover:bg-purple-50"
                onClick={() => isActive && processCommand(prompt.toLowerCase())}
              >
                "{prompt}"
              </Badge>
            ))}
          </div>
        </div>

        {/* Command history */}
        {commandHistory.length > 0 && (
          <div>
            <p className="text-xs text-gray-500 mb-2">Recent commands:</p>
            <ScrollArea className="h-[80px]">
              <div className="space-y-1">
                {commandHistory.slice().reverse().map((cmd, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-xs">
                    <span className="text-gray-400">
                      {cmd.time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                    <span className="text-gray-600">"{cmd.text}"</span>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        )}

        {/* Help panel */}
        {showHelp && (
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 mb-2">
                <HelpCircle className="h-4 w-4 text-blue-600" />
                <p className="text-sm font-medium text-blue-800">Available Commands</p>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setShowHelp(false)}
                  className="ml-auto h-6 w-6 p-0"
                >
                  ×
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs text-blue-700">
                <div className="flex items-center gap-1">
                  <CheckCircle className="h-3 w-3" />
                  "Approve all"
                </div>
                <div className="flex items-center gap-1">
                  <CheckCircle className="h-3 w-3" />
                  "Approve [med name]"
                </div>
                <div className="flex items-center gap-1">
                  <XCircle className="h-3 w-3" />
                  "Reject [med name]"
                </div>
                <div className="flex items-center gap-1">
                  <Pill className="h-3 w-3" />
                  "Show pending orders"
                </div>
                <div className="flex items-center gap-1">
                  <Sparkles className="h-3 w-3" />
                  "Send to pharmacy"
                </div>
                <div className="flex items-center gap-1">
                  <MicOff className="h-3 w-3" />
                  "Stop"
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Error display */}
        {error && (
          <div className="flex items-center gap-2 text-sm text-red-600">
            <AlertTriangle className="h-4 w-4" />
            <span>{error}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
